/**
 * 设备列表：左树（门店→位置→摄像头，含待分配）勾选后可批量推理 / 弹窗分配店铺；点摄像头右侧出详情。
 * 不做监控墙看图（无缩略图 / 抓拍 / 列表 SSE）。
 * 路由：/client/ys7/devices
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Space,
  Spin,
  Switch,
  Select,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message
} from 'antd'
import {
  CloudOutlined,
  ExperimentOutlined,
  LaptopOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined
} from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import Permission from '@/components/PermissionA5'
import YsDeviceDrawerBody from '@/views/ys/share/YsDeviceDrawerBody'
import ReloadClientPagesModal from '@/views/ys/share/ReloadClientPagesModal'
import BatchAssignYingshiDeviceStoreModal from '@/views/ys/share/BatchAssignYingshiDeviceStoreModal'
import { fetchYingshiDeviceList, patchYingshiDeviceNeedInference } from '@/redux/yingshi/deviceListSlice'
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'
import { updateYingshiDeviceNeedInference } from '@/service/yingshi'
import {
  mapYingshiDevicesToMergedList,
  buildYingshiDeviceDisplayGroups,
  isDeviceStoreUnassigned,
  YS_DEVICE_UNASSIGNED_STORE_KEY
} from './mergeAccountAndTrustDevices'
import './DeviceListPage.less'

const { Text } = Typography

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '设备列表' }
]

function deviceRowKey (record) {
  if (record?.id == null || record.id === '') return ''
  return String(record.id)
}

function camTreeKey (id) {
  return `cam:${id}`
}

function parseCamTreeKey (key) {
  const s = String(key || '')
  if (!s.startsWith('cam:')) return ''
  return s.slice(4)
}

function filterDevicesByKeyword (devices, keyword) {
  const q = (keyword || '').trim().toLowerCase()
  if (!q) return devices
  return devices.filter((d) => {
    const name = String(d?.name || '').toLowerCase()
    const serial = String(d?.deviceSerial || '').toLowerCase()
    const ch = String(d?.channelNo ?? '')
    const loc = String(d?.location || '').toLowerCase()
    return name.includes(q) || serial.includes(q) || ch.includes(q) || loc.includes(q)
  })
}

const DEVICE_LIST_QUERY = { includeExtendedFields: false }

export default function DeviceListPage () {
  const dispatch = useDispatch()
  const {
    data: yingshiDevices,
    fetching: deviceFetching
  } = useSelector((s) => s.yingshi.deviceList)
  const { default_storeOption: storeOption = [] } = useSelector((s) => s.dashboard.storeList) || {}

  const [filterStoreId, setFilterStoreId] = useState('')
  const [keyword, setKeyword] = useState('')
  const [onlyInferenceOff, setOnlyInferenceOff] = useState(false)
  const [checkedKeys, setCheckedKeys] = useState([])
  const [selectedKeys, setSelectedKeys] = useState([])
  const [expandedKeys, setExpandedKeys] = useState([])
  const [inferenceUpdatingId, setInferenceUpdatingId] = useState(null)
  const [batchUpdating, setBatchUpdating] = useState(false)
  const detailPaneRef = useRef(null)

  const loading = deviceFetching

  useEffect(() => {
    dispatch(fetchStoreList({ serviceType: 'default', dataType: 'storeOption' }))
    dispatch(fetchYingshiDeviceList({ query: DEVICE_LIST_QUERY }))
  }, [dispatch])

  const mergedDevices = useMemo(
    () => mapYingshiDevicesToMergedList(yingshiDevices),
    [yingshiDevices]
  )

  const deviceById = useMemo(() => {
    const map = new Map()
    for (const d of mergedDevices) {
      const id = deviceRowKey(d)
      if (!id) continue
      map.set(id, d)
    }
    return map
  }, [mergedDevices])

  const storeRowsOptions = useMemo(
    () =>
      (Array.isArray(storeOption) ? storeOption : []).map((item) => ({
        label: item.value,
        value: item.id
      })),
    [storeOption]
  )

  const storeSelectOptions = useMemo(
    () => [
      { label: '待分配', value: YS_DEVICE_UNASSIGNED_STORE_KEY },
      ...storeRowsOptions
    ],
    [storeRowsOptions]
  )

  const handleDeviceUpdated = () => {
    dispatch(fetchYingshiDeviceList({ query: DEVICE_LIST_QUERY }))
  }

  const handleAssignSuccess = () => {
    setCheckedKeys([])
    dispatch(fetchYingshiDeviceList({ query: DEVICE_LIST_QUERY }))
  }

  const storeNameById = useMemo(() => {
    const map = new Map()
    for (const item of Array.isArray(storeOption) ? storeOption : []) {
      if (item?.id == null || item.id === '') continue
      map.set(String(item.id), item.value || '')
    }
    return map
  }, [storeOption])

  const filteredDevices = useMemo(() => {
    let list = mergedDevices
    if (filterStoreId === YS_DEVICE_UNASSIGNED_STORE_KEY) {
      list = list.filter((d) => isDeviceStoreUnassigned(d.storeId))
    } else if (filterStoreId) {
      list = list.filter((d) => String(d.storeId) === String(filterStoreId))
    }
    list = filterDevicesByKeyword(list, keyword)
    if (onlyInferenceOff) {
      list = list.filter((d) => d.needInference !== true)
    }
    return list
  }, [mergedDevices, filterStoreId, keyword, onlyInferenceOff])

  const displayGroups = useMemo(
    () =>
      buildYingshiDeviceDisplayGroups({
        devices: filteredDevices,
        filterStoreId,
        storeNameById,
        preserveItemOrder: false
      }),
    [filteredDevices, filterStoreId, storeNameById]
  )

  const resolveFilteredStoreTitle = () => {
    if (!filterStoreId) return ''
    if (filterStoreId === YS_DEVICE_UNASSIGNED_STORE_KEY) return '待分配'
    const hit = storeSelectOptions.find((o) => String(o.value) === String(filterStoreId))
    return hit?.label || `店铺#${filterStoreId}`
  }

  const handleNeedInferenceChange = async (deviceRecord, checked) => {
    const id = deviceRecord?.id
    if (id == null || id === '') {
      message.error('设备 ID 缺失，无法设置推理')
      return
    }
    setInferenceUpdatingId(id)
    try {
      await updateYingshiDeviceNeedInference(id, { needInference: checked })
      dispatch(patchYingshiDeviceNeedInference({ id, needInference: checked }))
      message.success(
        checked ? '已开启推理，约 5 分钟后开始接收 AI 告警' : '已关闭推理'
      )
    } catch (error) {
      const errMsg =
        (error && typeof error === 'object' && (error.msg || error.message)) || '设置推理失败'
      message.error(String(errMsg))
    } finally {
      setInferenceUpdatingId(null)
    }
  }

  const renderCameraTitle = (d) => {
    const inferenceOn = d.needInference === true
    const isTrust = d.source === 'trust'
    const brandLabel = (d.brand || '').trim()

    return (
      <div className="ys-device-list-page__cam-row">
        <div className="ys-device-list-page__cam-main">
          <span className="ys-device-list-page__cam-name">{d.name || '—'}</span>
          <Text type="secondary" className="ys-device-list-page__cam-meta">
            {d.deviceSerial || '—'} · 通道 {d.channelNo ?? '—'}
          </Text>
          <Space size={4} wrap className="ys-device-list-page__cam-tags">
            {brandLabel ? (
              <Tag className="ys-device-list-page__brand-tag">{brandLabel}</Tag>
            ) : null}
            {inferenceOn ? (
              <Tag
                color="processing"
                icon={<ExperimentOutlined />}
                className="ys-device-list-page__inference-tag"
              >
                推理中
              </Tag>
            ) : (
              <Tag
                icon={<PauseCircleOutlined />}
                className="ys-device-list-page__inference-tag ys-device-list-page__inference-tag--paused"
              >
                暂停推理
              </Tag>
            )}
            {isTrust ? (
              <Tag color="geekblue" icon={<CloudOutlined />}>
                托管
              </Tag>
            ) : (
              <Tag color="green" icon={<LaptopOutlined />}>
                账号
              </Tag>
            )}
            <Tag color={d.status === 1 || d.status === '1' ? 'success' : d.status === 0 || d.status === '0' ? 'error' : 'default'}>
              {d.status === 1 || d.status === '1' ? '在线' : d.status === 0 || d.status === '0' ? '离线' : '—'}
            </Tag>
          </Space>
        </div>
        <div
          className="ys-device-list-page__cam-actions"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Tooltip
            title={inferenceOn ? '已开启 AI 推理，点击可关闭' : '开启后对该路摄像头进行 AI 推理'}
            mouseEnterDelay={0.3}
          >
            <span className="ys-device-list-page__row-inference">
              <Switch
                size="small"
                checked={inferenceOn}
                loading={inferenceUpdatingId === d.id || batchUpdating}
                disabled={d.id == null || d.id === '' || batchUpdating}
                aria-label="推理开关"
                onChange={(checked) => handleNeedInferenceChange(d, checked)}
              />
            </span>
          </Tooltip>
        </div>
      </div>
    )
  }

  const filteredStoreTitle = resolveFilteredStoreTitle()

  const treeData = useMemo(() => {
    return displayGroups.map((group) => {
      const storeKey = `store:${group.groupKey}`
      const storeTitle =
        group.groupKey === 'filtered' ? filteredStoreTitle : group.groupTitle || '门店'

      const locationNodes = group.sections.map((section) => {
        const locKey = `loc:${group.groupKey}:${section.sectionKey}`
        const camNodes = (section.items || [])
          .map((d) => {
            const id = deviceRowKey(d)
            if (!id) return null
            return {
              key: camTreeKey(id),
              isLeaf: true,
              title: renderCameraTitle(d)
            }
          })
          .filter(Boolean)
        return {
          key: locKey,
          selectable: false,
          title: (
            <span className="ys-device-list-page__node-title">
              {section.title}
              <Text type="secondary" className="ys-device-list-page__node-count">
                {camNodes.length} 台
              </Text>
            </span>
          ),
          children: camNodes
        }
      })

      return {
        key: storeKey,
        selectable: false,
        title: (
          <span className="ys-device-list-page__node-title">
            {group.isUnassigned || group.groupKey === YS_DEVICE_UNASSIGNED_STORE_KEY ? (
              storeTitle
            ) : (
              <>
                <ShopOutlined className="ys-device-list-page__store-ico" />
                {storeTitle}
              </>
            )}
            <Text type="secondary" className="ys-device-list-page__node-count">
              {locationNodes.reduce((sum, n) => sum + (n.children?.length || 0), 0)} 台
            </Text>
          </span>
        ),
        children: locationNodes
      }
    })
    // 摄像头行含 Switch/Tag，需随列表 patch 与单台 loading 重建
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayGroups,
    filteredStoreTitle,
    inferenceUpdatingId,
    batchUpdating,
    yingshiDevices
  ])

  const allParentKeys = useMemo(() => {
    const parents = []
    for (const group of displayGroups) {
      parents.push(`store:${group.groupKey}`)
      for (const section of group.sections) {
        parents.push(`loc:${group.groupKey}:${section.sectionKey}`)
      }
    }
    return parents
  }, [displayGroups])

  const parentKeysSig = allParentKeys.join('|')

  useEffect(() => {
    setExpandedKeys(allParentKeys)
  }, [filterStoreId, keyword, onlyInferenceOff, parentKeysSig])

  useEffect(() => {
    const selectedId = parseCamTreeKey(selectedKeys[0])
    if (!selectedId) return
    if (!filteredDevices.some((d) => deviceRowKey(d) === selectedId)) {
      setSelectedKeys([])
    }
  }, [filteredDevices, selectedKeys])

  const selectedDevice = useMemo(() => {
    const id = parseCamTreeKey(selectedKeys[0])
    if (!id) return null
    return deviceById.get(id) || null
  }, [selectedKeys, deviceById])

  useEffect(() => {
    if (detailPaneRef.current) {
      detailPaneRef.current.scrollTop = 0
    }
  }, [selectedKeys[0]])

  const selectedCameraIds = useMemo(() => {
    const ids = []
    for (const key of checkedKeys) {
      const id = parseCamTreeKey(key)
      if (id && deviceById.has(id)) ids.push(id)
    }
    return ids
  }, [checkedKeys, deviceById])

  const handleCheck = (keys) => {
    const list = Array.isArray(keys) ? keys : keys?.checked || []
    setCheckedKeys(list.filter((k) => String(k).startsWith('cam:')))
  }

  const handleSelect = (keys) => {
    const camKey = (keys || []).find((k) => String(k).startsWith('cam:'))
    if (camKey) {
      setSelectedKeys([camKey])
      return
    }
    // 点门店/位置不改变当前详情；再点已选摄像头可取消
    if (!keys || keys.length === 0) {
      setSelectedKeys([])
    }
  }

  const handleBatchInference = async (checked) => {
    if (selectedCameraIds.length === 0) {
      message.warning('请先勾选摄像头')
      return
    }
    const targets = selectedCameraIds
      .map((id) => deviceById.get(id))
      .filter((d) => d && d.needInference !== checked)
    if (targets.length === 0) {
      message.info(checked ? '所选设备均已开启推理' : '所选设备均已关闭推理')
      return
    }
    setBatchUpdating(true)
    let ok = 0
    let fail = 0
    for (const d of targets) {
      try {
        await updateYingshiDeviceNeedInference(d.id, { needInference: checked })
        dispatch(patchYingshiDeviceNeedInference({ id: d.id, needInference: checked }))
        ok += 1
      } catch {
        fail += 1
      }
    }
    setBatchUpdating(false)
    if (fail === 0) {
      message.success(
        checked
          ? `已开启 ${ok} 台推理，约 5 分钟后开始接收 AI 告警`
          : `已关闭 ${ok} 台推理`
      )
    } else {
      message.warning(`成功 ${ok} 台，失败 ${fail} 台`)
    }
  }

  const listEmptyDescription = useMemo(() => {
    if (mergedDevices.length === 0) return '暂无设备'
    if (onlyInferenceOff && filteredDevices.length === 0) return '没有未开启推理的设备'
    if (keyword.trim() && filteredDevices.length === 0) return '无匹配设备'
    if (filterStoreId === YS_DEVICE_UNASSIGNED_STORE_KEY) return '暂无待分配设备'
    if (filterStoreId) return '该店铺下暂无设备'
    return '暂无设备'
  }, [mergedDevices.length, filteredDevices.length, filterStoreId, keyword, onlyInferenceOff])

  const hasVisibleDevices = filteredDevices.length > 0
  const storeOptionsForModal = Array.isArray(storeOption) ? storeOption : []
  const noCameraChecked = selectedCameraIds.length === 0

  return (
    <div>
      <PageHeader breadcrumbList={breadcrumbList} title="设备列表" />
      <div className="i2-container">
        <div className="ys-device-list-page">
          <Spin
            spinning={loading && mergedDevices.length === 0}
            wrapperClassName="ys-device-list-page__spin"
          >
            {mergedDevices.length === 0 && !loading ? (
              <Empty className="ys-device-list-page__empty" description="暂无设备" />
            ) : (
              <>
                <section className="ys-device-list-page__toolbar i2-app-toolbar">
                  <div className="toolbar-filters">
                    <Select
                      allowClear
                      showSearch
                      placeholder="按店铺筛选"
                      className="ys-device-list-page__filter-store"
                      value={filterStoreId || undefined}
                      options={storeSelectOptions}
                      optionFilterProp="label"
                      onChange={(value) => {
                        setFilterStoreId(value || '')
                        setCheckedKeys([])
                        setSelectedKeys([])
                      }}
                    />
                    <Input
                      allowClear
                      prefix={<SearchOutlined />}
                      placeholder="名称 / 序列号 / 通道 / 位置"
                      className="ys-device-list-page__filter-keyword"
                      value={keyword}
                      onChange={(e) => {
                        setKeyword(e.target.value)
                        setCheckedKeys([])
                        setSelectedKeys([])
                      }}
                    />
                    <Checkbox
                      checked={onlyInferenceOff}
                      onChange={(e) => {
                        setOnlyInferenceOff(e.target.checked)
                        setCheckedKeys([])
                        setSelectedKeys([])
                      }}
                    >
                      仅未开推理
                    </Checkbox>
                    <Text type="secondary" className="ys-device-list-page__filter-hint">
                      门店 → 位置 → 摄像头 · 当前 {filteredDevices.length} 台
                      {mergedDevices.length !== filteredDevices.length
                        ? ` / 共 ${mergedDevices.length} 台`
                        : ''}
                    </Text>
                  </div>
                  <div className="toolbar-actions">
                    <Permission rootPath="ysDeviceList">
                      <span pathitem={['pageReload']} checkarray={['buttonCodes']}>
                        <ReloadClientPagesModal
                          baseProps={{
                            storeOptions: storeOptionsForModal
                          }}
                        >
                          <Button icon={<ReloadOutlined />}>刷新客户页面</Button>
                        </ReloadClientPagesModal>
                      </span>
                    </Permission>
                    <Text type="secondary">已选 {selectedCameraIds.length} 台</Text>
                    <BatchAssignYingshiDeviceStoreModal
                      disabledModal={noCameraChecked}
                      baseProps={{
                        deviceIds: selectedCameraIds,
                        storeOptions: storeOptionsForModal
                      }}
                      onSuccess={handleAssignSuccess}
                    >
                      <Button
                        icon={<ShopOutlined />}
                        disabled={noCameraChecked || batchUpdating}
                      >
                        分配店铺
                      </Button>
                    </BatchAssignYingshiDeviceStoreModal>
                    <Button
                      type="primary"
                      icon={<ExperimentOutlined />}
                      loading={batchUpdating}
                      disabled={noCameraChecked || batchUpdating}
                      onClick={() => handleBatchInference(true)}
                    >
                      开启推理
                    </Button>
                    <Button
                      icon={<PauseCircleOutlined />}
                      loading={batchUpdating}
                      disabled={noCameraChecked || batchUpdating}
                      onClick={() => handleBatchInference(false)}
                    >
                      关闭推理
                    </Button>
                  </div>
                </section>
                {!hasVisibleDevices && !loading ? (
                  <Empty
                    className="ys-device-list-page__empty"
                    description={listEmptyDescription}
                  />
                ) : (
                  <div className="ys-device-list-page__split">
                    <div className="ys-device-list-page__pane-left">
                      <Tree
                        checkable
                        selectable
                        blockNode
                        treeData={treeData}
                        checkedKeys={checkedKeys}
                        selectedKeys={selectedKeys}
                        expandedKeys={expandedKeys}
                        onCheck={handleCheck}
                        onSelect={handleSelect}
                        onExpand={(keys) => setExpandedKeys(keys)}
                        className="ys-device-list-page__tree"
                      />
                    </div>
                    <div className="ys-device-list-page__pane-right" ref={detailPaneRef}>
                      {selectedDevice ? (
                        <YsDeviceDrawerBody
                          key={deviceRowKey(selectedDevice)}
                          device={selectedDevice}
                          storeOptions={storeOptionsForModal}
                          onDeviceUpdated={handleDeviceUpdated}
                        />
                      ) : (
                        <Empty
                          className="ys-device-list-page__detail-empty"
                          description="点击左侧摄像头查看详情"
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </Spin>
        </div>
      </div>
    </div>
  )
}
